/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cdmiller
 */

import math from 'mathjs';
import nerdamer from 'nerdamer/all';

export const wrapDynFormula = function(Dyn) {

    // nodes in the formula: substrings that have meaning in the formula
    class Nodes {
        constructor( attributeName ) {
            // MathJS nodes from math.parse() then node.traverse()
            this.constants = []; // node.type ConstantNode
            this.operators = [];
            this.symbols = [];
            this.functions = [];
            this.other = []; // collect note.type names in case we need them in the future
            // other nodes created after parsing
            // this.selfSymbol = attributeName||""; // symbol for attribute that uses this formula, if any
            this.aggregates = []; // aggregate functions found by Formula.parse(), Aggregate.replaceAggregatesInString()
            this.inequalitySymbols = []; // inequalities found by Formula.fixFormulaInequalities()
            // nodes from findDependencies()
            this.clearDependencies( attributeName||"" );
        }

        findNodesMathJS( mathJsParseNode ) {
            mathJsParseNode.traverse( ( node /*, path, parent*/ ) => {
                switch (node.type) {
                    case 'OperatorNode':
                        this.operators.push( node.op );
                        break;    // operators like "+"
                    case 'ConstantNode':
                        this.constants.push( node.value );
                        break;    // numeric constants like "1.2"
                    case 'SymbolNode':
                        this.symbols.push( node.name );
                        break;   // possible attribute values like "age"
                    case 'FunctionNode':
                        this.functions.push( node.name );
                        break;   // MathJS function names like "pickRandom"
                    default:
                        this.other.push( node.type );
                }
            } );
        }

        findNodesNerdamer( nerdamerVariables ) {
            Dyn.assertValidArray( nerdamerVariables );
            this.symbols = nerdamerVariables;
        }

        clearDependencies( attributeName = "") {
            // clear derived lists, created by findDependencies()
            this.attributeSymbols = [];
            this.functionSymbols = [];
            this.aggregateSymbols = [];
            this.constantSymbols = [];
            this.plotSymbols = [];
            this.selfSymbol = attributeName;
            this.unknownSymbols = [];
            this.upstreamSymbols = []; // updated by findReverseDependencies()
        }
    }

    /**
     * Aggregate functions are handled by replacing a formula like 'mean( age )' with a constant like _agg_mean_age,
     * And supplying a value for that function in the scope of a formula evaluation, along with case attribute values.
     * This is the helper function to define which are the aggregate functions, and how they are calculated.
     */
    class Aggregate {
        constructor( formulaString, attributeName, functionName, calcFunction ) {
            this.formulaString = formulaString; // e.g. 'mean( age )' (as it appears in formula string)
            this.attributeName = attributeName; // e.g. 'age'
            this.functionName = functionName; // e.g. 'mean'
            this.func = calcFunction; // e.g. reference to mean(numericArray) function
            this.variableName = "_"+functionName+"_"+attributeName; // e.g. '_mean_age'
            Dyn.assert( typeof calcFunction === 'function' );
        }
        static debug = Dyn.debugDataInput && false; // add debugging if &debug=3+

        calculate( numericValues ) {
            let result = (numericValues.length >= 1) ?
                    Formula.fixPrecision( this.func( numericValues ), Dyn.dataUtil.kMissingValue) :
                    Dyn.dataUtil.kMissingValue;
            return result;
        }

        /** find the aggregeate function-attribute pairs in the formula, replace them with constants,
         * and return both the new formula string and the Aggregate objects to calculate the constant values.
         * e.g. replace 'mean(age)*10' with '_mean_age*10' and return the Aggregate object for mean(age)
         * @param formulaString
         * @returns {{aggregates: *[], formulaString: *}|null}
         */
        static replaceAggregatesInString( formulaString )
        {
            let aggregates = [],
                newString = formulaString.replace( new RegExp( Aggregate.getPattern(), 'g' ), replacer );
            Aggregate.debug && console.log( "replaceAggregatesInString", (newString!==formulaString)?"new string: "+newString:"unchanged: "+formulaString );
            if( aggregates.length ) {
                return { aggregates:aggregates, formulaString:newString };
            } else {
                return null;
            }

            function replacer( match, p1FunctionName, p2AttributeName, offset, string ) {
                // Aggregate.debug && console.log("replaceAggregatesInString found", match, p1FunctionName, p2AttributeName, offset, "in", string );
                let agg = new Aggregate( match, p2AttributeName, p1FunctionName, Aggregate.functions[p1FunctionName] );
                aggregates.push( agg ); // save for use by calcAggregates()
                return agg.variableName; // replace match with variable name, for use by calcAggregates()
            }
        }
        static pattern = ""; // find "..mean( attribute ), etc.. in strings; build once and store here
        static getPattern() {
            if( ! Aggregate.pattern ) {
                // build Regular Expression pattern to find" aggregate-function ( attribute ) " pairs,
                //      with whitespace before and after every parenthesis (to match what MathJS normal functions allow).
                //      with [a-zA-Z0-9_] as allowable function and attribute characters (as returned by _attributeIDtoSymbol())
                // Example: '...mean( age_attr )' matches
                //          '...count (height_attr)' matches
                //          ' log( age_attr )' does not match ('log' is not an aggregate function).
                let names = Object.keys( Aggregate.functions ).join( '|' );
                Aggregate.pattern = '(' + names + ')\\s*\\(\\s*(\\w+)\\s*\\)';
            }
            return Aggregate.pattern;
        }
        static functions = {
            // define the aggregate function names and formulas, for use in derived attribute formulas
            // all take an array of finite numeric values, 1+ length, with non-numeric/missing values removed
            // warning: changing function names will break previously saved formulas.
            mean:   ( a ) => { return Dyn.mathUtil.meanOfNumericArray( a ) },
            median: ( a ) => { return Dyn.mathUtil.medianOfNumericArray( a ) },
            iqr:    ( a ) => { return Dyn.mathUtil.quartilesOfNumericArray( a ).IQR },
            stdDev:    ( a ) => { return Dyn.mathUtil.stDevOfNumericArray( a ) },
            meanAvDev:    ( a ) => { return Dyn.mathUtil.meanAvDevOfNumericArray( a ) },
            variance: ( a ) => { return Dyn.mathUtil.varianceOfNumericArray( a ) },
            sum:    ( a ) => { return Dyn.mathUtil.sumOfNumericArray( a ) },
            min:    ( a ) => { return a.reduce( (g,h) => Math.min(g,h), a[0] ); },
            max:    ( a ) => { return a.reduce( (g,h) => Math.max(g,h), a[0] ); },
            count:  ( a ) => { return a.length },
        }
    }

    class Formula {

        /**
         * Constructor for a formula used to create derived attributes or to plot a function curve
         * @param formulaString
         * @param formulaSyntax -- {"LaTeX" || "MathJS" || null } -- defaults to MathJS, or converted using latexFormulaToMathJS()
         * @param constantScope -- optional numeric or string constant values to add to scope, e.g. { a:1, name:"jane" }
         * @param formulaID -- optional formula ID
         */
        constructor( formulaString="", formulaSyntax, constantScope={} /*, formulaID */ ) {
            // original formula string and syntax
            this.formulaString = formulaString || ""; // string value of formula
            this.formulaSyntax = formulaSyntax;
            Dyn.assert( ['LaTeX', 'MathJS'].includes(formulaSyntax), "Formula syntax must be 'LaTeX' or 'MathJS'" );

            // derived formula state
            // this.mathJsCompiled = null; // MathJS compiled formula
            // this.derivativeFormula = null; // MathJS-derived slopeOfFx formula string
            // this.derivativeCompiled = null; // MathJS-derived slopeOfFx compiled formula
            this.nodes = null; // objects in parsed formula
            this.error = {};   // last error string, of various types
            this.constantScope = constantScope;   // map of constant name:value pairs where name is a string, value is a finite number.
            this.numberOfEvaluations = 0; // for debugging

            // if( ! math.hasTuvaExtentions ) {
            //     this.extend_MathJS_Library();
            // }
        }

        // static const
        static debug = Dyn.debugDataInput && true; // add debugging if &debug=3+
        // static useNerdamerOnly = true;  // turn on for efficiency, turn off to revert to MathJS evaluation
        // static compareNerdamerToMathJS = true;      // turn on for debugging the difference between MathJS and Nerdamer
        static debugShowDivideByZeroErrors = false;      // turn on to see divide by zero errors

        // from: http://mathjs.org/docs/reference/constants.html
        //      Note: this list determines which MathJS constants can be used in Tuva attribute formulas
        //      we exclude less useful formulas like "null" so we don't have to explain them to users;
        //      for now only numeric constants seem useful.  --CDM 2017-05-16
        get _mathJS_Constants()  { return [ 'e', 'E', 'i', 'pi', 'PI', 'LN2', 'LN10', 'LOG2E', 'LOG10E', 'NaN', 'phi', 'tau' ] }

    /** our custom modifications to MathJS will go here */
    extend_MathJS_Library() {
        // add constants and functions as described in: http://mathjs.org/docs/core/extension.html
        Dyn.assert( ! math.hasTuvaExtentions );
        math.import({
            hasTuvaExtentions: true,
            //myvalue: 42,

            length: function( stringOrArray ) {
                let result = "not text";
                switch( typeof stringOrArray ) {
                case "string":
                    result = stringOrArray.length;
                    break;
                case "object":
                    if( Array.isArray( stringOrArray ) ) {
                        result = stringOrArray.length;
                    }
                    break;
                }
                return result;
            }
        });
    }

    isEmptyFormula() {
        return this.formulaString.length === 0;
    }

    getLastErrorString( errorFilterString ) {
        var error = this.error,
            str;

        switch( errorFilterString ) {
        case "parseErrorsOnly":
            str = error.parse || ""; // show long message if available
            break;
        case "dependencyErrorsOnly":
            str = error.dependency || "";
            break;
        case "notEvalErrors":
            str = error.parse || error.dependency || ""; // show last error if any
            break;
        default:
            str = error.parse || error.dependency || error.evaluate || ""; // show last error if any
        }
        return str;
    }

    /**
     * Compile formula if needed, then check for errors:
     * Returns true if syntax or unknown symbol or depencency loop error
     * @param optionalDataSet
     * @returns {boolean}
     */
    checkForSyntaxOrReferenceError( optionalDataSet ) {
        this.findDependencies( optionalDataSet );
        return (this.getLastErrorString("notEvalErrors").length > 0); // ignore last evaluation error, if any
    }

    /** check for evaluation errors, and if any, return the number of total evaluations, and number which had errors.
     *  For example a formula may divide by some expression, resulting in 8 divide-by-zero errors in 100 cases. */
    checkforEvaluationError() {
        if( this.error.evaluate ) {
            return { total: this.evalCount(), errors: this.evalErrorCount() };
        }
        return null;
    }

    /**
     * Use MathJS to parse the formula string, get symbols.
     * @param [dataSet] {Dyn.DataSet || null } -- dataSet, required only if allowing attribute names in formula
     * @param [attributeName] {Dyn.Attribute || null } -- optional attribute name for error messages
     */
    // parse( dataSet, attributeName ) {
    //     // initialize nodes and error state
    //     this.nodes = new Nodes( attributeName );
    //     this.error = {};
    //
    //     // parse, find nodes, compile formula, and record any errors, in preparation for evaluation
    //     if( this.isPlotFormula ) {
    //         this.parseNerdamer(); // nerdamer used for F(x) plot formulas, aka Model Functions
    //     } else {
    //         Dyn.assert( this.isAttrFormula );
    //         this.parseMathJS( dataSet, attributeName ); // MathJS used for attribute formulas
    //     }
    // }

    /**
     * Calculate the aggregate function-attribute pairs found in the formula, and save as constants in the scope
     * For example: if '100-mean(age)' is the formula, and the mean of age is 1.23, add _mean_age:1.23 to the scope.
     * This works with findAggregates() which modifies the formula to replace 'mean(age)' with the constant '_mean_age',
     * so the evaluated formula is '100-_mean_age' with scope { _mean_age: 1.23, age: 20, ... }.
     * @param dataSet
     * @param scope
     */
    calcAggregates( dataSet, scope ) {
        Dyn.assert( this.isAttrFormula );
        this.nodes.aggregates.forEach((agg) => {
            let attribute = Formula._getAttribute( agg.attributeName, dataSet ),
                numericValues = attribute ? attribute.cases.filter( (v) => Number.isFinite(v)) : [];
            scope[ agg.variableName ] = agg.calculate( numericValues );
            Formula.debug && console.log("Formula.calcAggregates", agg.variableName, scope[ agg.variableName ], this.formulaString );
        });
    }

    /**
     * Compile the formula, find dependencies (symbols) in the dataSet, set or clear the error state.
     * Must determine which symbols are valid attribute names, constants (pi, e), which are not.
     *   Updates:
     *      this.mathJsCompiled
     *      this.nodes.***Symbols (.attributeSymbols, .constantSymbols, etc).
     *      this.error.dependency
     * Note: Can be extended to find functions and operators in the formula (not yet needed)
     * @param [dataSet] {Dyn.DataSet||null} -- dataset which has attributes which we can reference
     * @param [attribute] {Dyn.Attribute||null} -- attribute which uses this formula
     * @returns {{}} - MathJS scope object with all values undefined, for use by getCaseScope() or getPlotFunctionScope().
     */
    findDependencies( dataSet, attribute ) {
        // compile the formula if needed (to be cleared when formula string changes)
        let attributeName = attribute ? Formula._attributeIDtoSymbol( attribute.id ) : "";
        if( this.isAttrFormula ) {
            if( !this.mathJsCompiled ) {
                this.parse( dataSet, attributeName );
            }
        }
        if( this.isPlotFormula ) {
            if( ! this.nerdamerString ) {
                this.parse( dataSet, attributeName );
            }
        }
        this.error.dependency = ""; // clear the dependency error state, unless we find an error below

        // add each symbol in formula to the appropriate list (attributeSymbols, etc)
        // so that any remaining symbols are marked as unknown, to generate error messages
        // update error.dependency state, save scope.
        let scope = {}; // symbol definitions for MathJS.eval()
        this.nodes.clearDependencies( attributeName ); // reset symbol list with updated attribute name if renamed
        this.nodes.symbols.forEach( ( symbol ) => {
            let attribute = dataSet && Formula._getAttribute( symbol, dataSet );
            if( attribute ) {
                // symbol matches an attribute
                this.nodes.attributeSymbols.push( symbol );
                // add a placeholder for this attribute in the scope (used later by getCaseScope())
                scope[symbol] = Dyn.dataUtil.kMissingValue;
            } else if( this._mathJS_Constants.includes( symbol )) {
                // MathJS constant like 'pi'; save it in our list and do not treat as an error
                this.nodes.constantSymbols.push( symbol );
            } else if( this.constantScope.hasOwnProperty( symbol )) {
                // symbol is an f(x) constant/variable like 'm','x','b', see also getPlotFunctionScope()
                this.nodes.plotSymbols.push( symbol );
                scope[symbol] = this.constantScope[symbol]; // value of constant or placeholder for variable
            } else if( this.nodes.functions.includes( symbol )) {
                // functions like 'abs', which are included in both nodes.functions and nodes.symbols; see FindNodes()
                this.nodes.functionSymbols.push( symbol );
            } else if( this.nodes.aggregates.find((agg)=>(agg.variableName===symbol))) {
                // aggregate constants like '_mean_age', created by Aggregate.replaceAggregatesInString()
                this.nodes.aggregateSymbols.push( symbol );
            } else {
                // save unknown symbols and report the first one as an error
                if( ! ( this.error.parse || this.error.dependency )) {
                    this.error.dependency = Dyn.loc( "Unknown symbol: {{symbol}}", { symbol: symbol } );
                }
                this.nodes.unknownSymbols.push( symbol );
            }
        });
        return scope;
    }

        /**
         * return true if this formula depends on the given attribute. Requires that findDependencies()
         * has been called since the formula was created. Finds only direct dependencies, not indirect ones,
         * so caller should call this recursively to find all dependencies.
         * @param attributeID {Dyn.Attribute.id}
         */
    isDependentOn( attributeID ) {
        return !! this.nodes?.attributeSymbols?.includes( Formula._attributeIDtoSymbol( attributeID ));
    }

    // disabled, this is flawed and not needed, see issue #2637
    /**
     * Get the attribute value format from our parsed formula
     *  format.decimals:0 means display as integer
     *  format.decimals:2 means round to two fixed decimal places
     *  format.decimals:undefined means no numeric format
     * @param dataSet {Dyn.DataSet}
     * @returns {{formulaDecimals: *}}
     */
    // getValueFormat( dataSet ) {
    //     Dyn.assert( this.nodes, "Formula has not been parsed" );
    //     var newDecimals,
    //         oldDecimals;
    //
    //     // scan attributes we reference, get the maximum number of significant decimals to display
    //     this.nodes.attributeSymbols.forEach( function( symbol ) {
    //         var attribute = Formula._getAttribute( symbol, dataSet );
    //         if( attribute ) {
    //             // save significant digit information from attributes we depend on, if any
    //             newDecimals = attribute.getNumericDecimals();
    //             if( newDecimals && oldDecimals ) {
    //                 oldDecimals = Math.max( oldDecimals, newDecimals );
    //             } else {
    //                 oldDecimals = newDecimals;
    //             }
    //         }
    //     } );
    //     return { formulaDecimals: newDecimals }; // undefined or 0+
    // }

    /**
     * Find other attribute-formulas in the dataset that reference this attribute.
     * Updates: this.nodes.upstreamSymbols
     * @param dataSet {Dyn.DataSet}
     * @param thisAttribute {Dyn.Attribute}
     */
    findReverseDependencies( dataSet, thisAttribute  ) {
        // if parse error, we will not have a compiled formula, but nodes will initialized (and empty)
        Dyn.assert( this.isAttrFormula );
        Dyn.assert( this.nodes, "Formula has not been parsed");
        Dyn.assert( this.nodes.upstreamSymbols.length === 0 );
        Dyn.assert( thisAttribute.formula === this );
        var self = this,
            selfSymbol = this.nodes.selfSymbol = Formula._attributeIDtoSymbol( thisAttribute.id );

        dataSet.attribs.forEach( function ( otherAttribute ) {
            var otherFormula = otherAttribute.formula;
            if ( otherFormula && otherFormula.nodes.attributeSymbols.indexOf( selfSymbol ) !== -1 ) {
                var otherSymbol = Formula._attributeIDtoSymbol( otherAttribute.id );
                self.nodes.upstreamSymbols.push( otherSymbol );
            }
        } );
    }

    /**
     * Add zero or more formula attributes to the stack in the correct evaluation order,
     *      starting with the given attribute.  Designed to be called repeatedly if desired,
     *      e.g. to sort all formula attributes in the dataset.
     * If called for all attributes in the dataset, we want isUpstreamSearch===false,
     *      for a depth-first sort.
     * If called for a single attribute to find what other formula attributes depend on its values,
     *      we want isUpstreamSearch===true, for a depth-last sort.
     *
     * Details: if isUpstreamSearch==false, the given attribute is added only if it is a formula attribute.
     *      And if it references other attributes, those attributes are added first .
     * Example: Given stack [a] (from previous call), and attribute b which references c and d,
     *      and d is a formula attribute that references e,
     *      and all are formula attributes except for c,
     *      then stack will change to [a, e, d, b].
     *
     * @param attribStack {[Dyn.Attribute]} -- stack of formula attributes to evaluate from first to last
     * @param attribsVisited {[Dyn.Attribute]} -- list of formula attributes already visited (may or may not be in stack)
     * @param attr {Dyn.Attribute} -- attribute to start with (must be the attribute that owns this formula)
     * @param dataSet {Dyn.DataSet} -- the dataset that owns this attribute and formula
     */
    addAttributeToStackEvaluateOrder( attribStack, attribsVisited, attr, dataSet, isUpstreamSearch ) {

        function addFormulaAttributeDepthFirst( attr, isUpstreamSearch )
        {
            if ( attr.formula ) { // must be formula attribute
                attribsVisited.push( attr ); // visit attribute only once to avoid infinite loops
                if( isUpstreamSearch ) {
                    attribStack.push( attr );   // add depth-last if upstream search, so dependent attributes come after
                }
                var theNodes = attr.formula.nodes,
                    symbolList = isUpstreamSearch ? theNodes.upstreamSymbols : theNodes.attributeSymbols;
                symbolList.forEach( function ( symbol ) {
                    var referencedAttribute = Formula._getAttribute( symbol, dataSet );
                    if ( notVisited( referencedAttribute ) ) {
                        addFormulaAttributeDepthFirst( referencedAttribute, isUpstreamSearch );
                    }
                } );
                if( ! isUpstreamSearch ) {
                    attribStack.push( attr );   // add depth-first if downstream search, so dependent attributes come after
                }
            }
        }
        function notVisited( attr ) {
            return ( attribsVisited.indexOf( attr ) === -1 );
        }

        // recursive call to add attribute and its dependent formula-attributes to the stack
        Dyn.assert( this.nodes, "Formula has not been parsed");
        Dyn.assert( attr.formula === this, "must start with the attribute that owns this formula");
        addFormulaAttributeDepthFirst( attr, isUpstreamSearch );
    }

    /**
     * Check for depencency loops with formulas, where a references b and b references a.
     * If the given list of attributes is one we depend on then the next evaluate() should give an error.
     * @param attributeStack -stack of attribute names in evaluation order, created by addAttributeToStackEvaluateOrder()
     */
    checkForDependencyLoops( attributeStack, dataSet ) {
        Dyn.assert( this.isAttrFormula );
        // for each attribute that this formula references
        this.nodes.attributeSymbols.forEach( ( symbol ) => {
            // if attribute is in attribute stack, report a depencency loop
            var attribute = Formula._getAttribute( symbol, dataSet );
            if( attributeStack.indexOf( attribute ) !== -1 ) {
                this.error.dependency = Dyn.loc( "Dependency loop with {{symbol}}", { symbol: symbol } );
                Formula.debug && console.warn( "formula dependency error {"+this.error.dependency+"} for "+this.nodes.selfSymbol );
            }
        }, this);
    }

    // setConstant( name, value ) {
    //     Dyn.assert( typeof name === "string" );
    //     Dyn.assert( typeof name === "string" || typeof name === "number" || typeof name === "boolean");
    //     this.constantScope[ name ] = value;
    // }

    /**
     * Evaluate the formula with the given scope, that resolves the value of referenced attributes
     * in the scope, e.g. { age:10, height:5.5 }. If a error.parse already set,
     * don't evaluate, just return the error until it is cleared.
     * Usually the caller evaluates the formula once for each case of an attribute.
     * @param scope {*} -- attributes and values referenced by MathJS formula
     * @returns {*} -- a number or string or undefined value; errors are shown as string values.
     */
    evaluate( scope, expectedType ) {
        var result;
        this.error.evaluate = ""; // clear last evaluate error, if any

        if ( this.getLastErrorString("notEvalErrors") || scope.has__missing__values ) {
            result = Dyn.dataUtil.kMissingValue; // if parse or dependency error, or if missing values in scope, show result as missing value (blank)
        } else {
            if( this.isPlotFormula ) {
                result = this.evaluateNerdamer( scope, expectedType );
            } else {
                result = this.evaluateMathJS( scope, expectedType );
            }
        }
        return( Dyn.dataUtil.normalizeCaseValueString( result )); // make sure result is string, number or undefined
    }

    /**
     * NOTE: UNUSED function, save for reference, but perhaps remove altogether?
     * rootsAndInterceptsOfFx() uses nerdamer() instead, can we do the equalalent with MathJS?
     *
     * Get the slope of the function at the give x,y point.
     * @param x -- x value of F(x) function
     * @param y -- y value of F(x) function; assumes we called Evaluate() first to get this.
     * @param scope -- scope from GetPlotFunctionScope
     * @returns {number|NaN}
     *
    slopeOfFx( x, y, scope ) {
        Dyn.assert( this.isPlotFormula, "expecting a plot formula"); // TODO: move to class PlotFormula
        // reference: https://mathjs.org/docs/reference/functions/derivative.html
        //            e.g. math.derivative('x^2', 'x').evaluate({x: 4})'
        Dyn.assert( this.mathJsString, "expecting a MathJS formula");
        let slope;
        if( Dyn.mathUtil.isNumber(x) &&
            Dyn.mathUtil.isNumber(y) && // if y not finite, don't bother with slope
            this.mathJsCompiled ) {    // formula must have been parsed already
            // parse and compile the derivative once only
            if( ! this.derivativeCompiled ) {
                try {
                    let node = math.derivative( this.mathJsString, 'x' );
                    this.derivativeCompiled = node.compile();
                } catch ( err ) {
                    Formula.debug && console.log( "formula derivative parse error {" + err.message + "} for " + this.formulaString );
                    this.derivativeCompiled = null;
                }
            }
            // calculate the slope by evaluating the derivative at x
            if( this.derivativeCompiled ) {
                try {
                    this.numberOfEvaluations++; // for debugging
                    slope = this.derivativeCompiled.eval( scope );
                }
                catch ( err ) {
                    Formula.debug && console.log( "formula derivative evaluate error {"+err.message+"} for "+this.formulaString );
                }
            }
        }
        return slope; // caller should check Dyn.mathUtil.isNumber(slope);
    }
    **/

    /**
     * Convert attribute ID string to symbol for Math.js parsing
     * Symbol: a Math.js variable name; Math.js disallows underscores, names starting with a digit, etc.
     * @param attributeID {string}
     * @returns {string}
     * @private
     */
    static _attributeIDtoSymbol( attributeID ) {
        // attribute IDs are created by sluggify() function
        // reference for valid characters in MathJS variable names: https://github.com/josdejong/mathjs/issues/688
        return attributeID
                .replace(/\-/g, '_') // replace dash with underbar, so it is not interpreted as a minus operator
                .replace(/^([0-9])/g, "_$1"); // add underbar if the first character is a digit
    }

    /**
     * Find the attribute object in the dataset that matches this symbol name.
     * @param attributeSymbol {string} -- attribute symbol from _attributeIDtoSymbol()
     * @param dataSet {Dyn.DataSet}
     * @returns {Dyn.attribute||undefined}
     * @private
     */
    static _getAttribute( attributeSymbol, dataSet ) {
        // attribute symbols have underbar, attribute IDs have dash or underbar.
        // Note: we can't use: symbol.replace(/_/g, '-')
        //       because we don't know if dash should be an underbar or stay a dash.
        return dataSet.attribs.find( (attr) => (attributeSymbol === Formula._attributeIDtoSymbol( attr.id )));
    }

    static latexToMathJsOrNerdamer = [
        [/\\cdot/g, '*'],   // multiplication symbol: \cdot => *
        [/{/g, '('], [/}/g, ')'], // curley brackets {} with ()
        [/\\left\(/g, '('],
        [/\\right\)/g, ')'], // \left and \right with ()
        [/\\left\|/g, 'abs('],
        [/\\right\|/g, ')'], // \left| and \right| with abs()
        [/\\le/g, '<='],
        [/\\ge/g, '>='], // \le and \ge with <= and >=
        [/\ /g, ' '], // remove \ before spaces
        [/\\/g, ''], // do last, remove \
    ]

    latexFormulaToMathJS( formulaString) {
        let mjsString = formulaString
            .replace(/\\sqrt\[(.*?)\]\{(.*?)\}/g, 'nthRoot($2,$1)') // nth root '\sqrt[n]{x}\ ' => 'nthRoot(x,n)'
            .replace(/(\\frac){(.*?)}{(.*?)}/g, '(($2)/($3))');    // division '\frac{a}{b}' => '((a)/(b))' || 'divide((a),(b))'
        Formula.latexToMathJsOrNerdamer.forEach( ([ r, s ]) => {
            mjsString = mjsString.replace( r, s );
        });
        Formula.debug && console.log("LaTeX formula '", formulaString, "' convert to MathJS '", mjsString, "'" );
        return mjsString;
    }

    latexFormulaToNerdamer( formulaString) {
        let nerdString = formulaString
            .replace(/\\sqrt\[(.*?)\]\{(.*?)\}/g, '(($2)^(1/($1)))') // nth root '\sqrt[n]{x}\ ' => '((x)^(1/(n))'
            .replace(/(\\frac){(.*?)}{(.*?)}/g, '(($2)/($3))');    // division '\frac{a}{b}' => '((a)/(b))' || 'divide((a),(b))'
        Formula.latexToMathJsOrNerdamer.forEach( ([ r, s ]) => {
            nerdString = nerdString.replace( r, s );
        });
        try {
            // *DON'T USE, replaces "k/x" with "k*x^(-1)"
            //nerdString = nerdamer.convertFromLaTeX( nerdString ).text();
        } catch ( err ) {
            Formula.debug && console.log( "formula error in nerdamer.convertFromLaTeX() {" + err.message + "} for " + formulaString );
            this.error.parseNerdamer = err.message;
            nerdString = "";
        }
        // Formula.debug && console.log("LaTeX formula '", formulaString, "' convert to Nerdamer '", nerdString, "'" );
        return nerdString;
    }

    /** reset counts for error reporting and debugging */
    evalCountStart() {
        this.numberOfEvaluations = 0; // total number of formula evaluations for this round
        this.numberOfEvalErrors = 0; // number of these evaluations that resulted in an error
    }

    evalCount() {
        return this.numberOfEvaluations;
    }

    evalErrorCount() {
        return this.numberOfEvalErrors;
    }

    /** limit the calculated values from formulas
     *   - in floating-point precision (to avoid calculation roundoff errors after 15 decimal places)
     *   - in decimal places after zero (to avoid scientific notation in calculations, e.g. sin(pi)=0 not 1e-16
     */
    static fixPrecision( num, valueIfNotFiniteNumeric ) {
        return Number.isFinite( num ) ?
            parseFloat( parseFloat( num.toPrecision( Dyn.mathUtil.kStatsMaxPrecision )).toFixed( Dyn.mathUtil.maxCalcFixedDecimals)) :
            valueIfNotFiniteNumeric;
    }

}  // class Formula

    /** formula for plotting curves and inequalities */
    class PlotFormula extends Formula {
        constructor( formulaString="", constantScope={}, formulaID=0, formulaSyntax='LaTeX', lineVisible=true  ) {
            super( formulaString, formulaSyntax, constantScope );

            if( this.formulaSyntax === 'LaTeX' ) {
                this.latexString = formulaString; // save for addToMeasures()
            }
            if( formulaID ) {
                this.id = "" + formulaID; // optional formula ID, 1+, force to string
            }

            // derived formula state
            this.nerdamerString = "";
            this.nerdamerCompiled = null; // nerdamer( this.nerdamerString ) parse result
            this.lineVisible = (typeof lineVisible==='boolean') ? lineVisible : true; // default to true
        }

        get isPlotFormula() { return true; }
        get isLineVisible() { return this.lineVisible; }

        setLineColor( lineColor ) {
            this.lineColor = lineColor;
        }

        /**
         * Update formula with to separate 2-letter symbols with x or y, to correctly handle implicit multiplication.
         * Example: 'mx+b' to 'm x+b', 'xa + yb = c" to 'x a + y b = c'
         * NOTE: '2x+b', '(m)x+b', 'm x + b' are already handled as implicit multiply by Nerdamer
         */
        fixFormulaImplicitMultiply( str ) {
            // regular expression that matches any two-letter word starting or ending with 'x' or 'y', e.g. 'mx', 'ny', 'xa', 'yb'
            // but not 'ab', 'mxy', etc.
            let s2 = str.replaceAll(/\b([a-zA-Z])([xy])\b/g,"$1 $2").replaceAll(/\b([xy])([a-zA-Z])\b/g,"$1 $2");
            Formula.debug && (s2 !== str) && console.log( "found implicit multiply in formula: "+str, 'now: '+s2 );
            return s2;
        }

        static enableInequalities() { return true; } // to enable/disable inequalities, until feature is complete
        static enableSolveForY() { return true; } // to enable/disable solveForY, until feature is complete
        get isInequality() { return this.nodes.inequalitySymbols.length === 1; }
        getInequalitySymbol() { return this.nodes.inequalitySymbols[0]; }
        isDashedInequality() { return this.isInequality && /^(>|<)$/.test(this.getInequalitySymbol()); }
        isInequalityGtOrGe() { return this.isInequality && /^(>=?)$/.test(this.getInequalitySymbol()); }

        /**
         * find any inequality symbols (>, >=, <, <=) and if found, extract it and the string modified to use '='.
         * Note: only the first symbol is returned, multiple inequalities are not supported (user should get formula error)
         * e.g. "y<=2x+3" => { formulaString: "y=2x-3", inequalitySymbols: ["<="] }
         */
        fixFormulaInequalities( str ) {
            if( ! PlotFormula.enableSolveForY() ) {
                return null;
            }
            let symbols = [],
                inequalityMatch = /(>=?|<=?)/g.exec( str ); // has '>', '>=', '<', '<='
            if( inequalityMatch && str.includes('y') ) {
                // save the inequality symbol, and replace it with '=' (e.g. 'x+y<=3' => 'x+y=3')
                // because further parsing expects a function of x, or to solve for y=...
                symbols.push( inequalityMatch[1] );
                str = str.replaceAll( inequalityMatch[1], '=' );
            }
            if( symbols.length > 0 ) {
                Formula.debug && console.log( "found inequality in formula: "+str, symbols );
                return { formulaString: str, inequalitySymbols: symbols };
            }
            return null; // none found
        }

        /**
         * Find the unknown symbols in the formula, and update the error state.
         * @param optionalDataSet
         * @returns {Array} -- array of names (strings)
         */
        findUnknownSymbols( optionalDataSet ) {
            // compile if needed, then return unknown symbols.
            this.findDependencies( optionalDataSet );
            return this.nodes.unknownSymbols;
        }

        /**
         * Use Nerdamer to parse the formula string, get symbols.
         * @param [dataSet] {Dyn.DataSet || null } -- dataSet, required only if allowing attribute names in formula
         * @param [attributeName] {Dyn.Attribute || null } -- optional attribute name for error messages
         */
        parse( dataSet, attributeName ) {
            // initialize nodes and error state
            Dyn.assert( this.formulaSyntax === 'LaTeX');
            this.nodes = new Nodes( attributeName );
            this.error = {};

            // modify the formulaString to be Nerdamer compatible
            this.nerdamerString = this.latexFormulaToNerdamer( this.formulaString );
            this.nerdamerString = this.fixFormulaImplicitMultiply( this.nerdamerString );
            this.symbolSortString = this.nerdamerString; // save string before solveFor() re-arranges the symbol order
            // Formula.debug && console.log( nerdamer.convertToLaTeX(this.nerdamerString).toString())

            // modify the formulaString to extract inequalities, replace with '='
            let inEqs = this.fixFormulaInequalities(this.nerdamerString);
            if( inEqs ) {
                this.nerdamerString = inEqs.formulaString;
                this.nodes.inequalitySymbols = inEqs.inequalitySymbols;
            }

            // Nerdamer parse and analyze nodes of the formula (to find variables).
            if( this.nerdamerString ) {
                this.nerdamerCompiled = null;
                try {
                    if( this.nerdamerString.trim() === 'y' ) {
                        throw {}; // show "Formula incomplete", otherwise it parses fine
                    }
                    this.nerdamerCompiled = nerdamer( this.nerdamerString );
                    let v = this.nerdamerCompiled.variables();
                    if( PlotFormula.enableSolveForY() && v.includes('y')) {
                        // solve for y=... as a function of x, or as a constant (y=sin(12)), then recompile
                        let s0 = this.nerdamerString;
                        this.nerdamerString = this.nerdamerCompiled.solveFor('y').toString();
                        this.nerdamerCompiled = nerdamer( this.nerdamerString );
                        v = this.nerdamerCompiled.variables();
                        Formula.debug && console.log("solveFor: y = F(x) :", s0, "=>", this.nerdamerString, "vars:", v );
                        if( this.nerdamerString.includes(',')) {
                            // Formula.debug && console.warn( "formula not supported: multiple y values in formula", this.nerdamerString );
                            throw { name:'tuva', message:Dyn.loc("Function not supported")}; // e.g. y^2=x
                        }
                    }
                    this.nodes.findNodesNerdamer( v ); // 2nd pass: find unknown symbols, etc.
                }
                catch(err) {
                    // Formula.debug && console.warn( "formula parse error {"+err.message+"}", this.nodes.selfSymbol );
                    this.error.parse = this._errorMessageToLongString( err );
                }
                Formula.debug && console.log("Dyn.Formula.parseNerdamer:", this.formulaString, this.nerdamerCompiled?.toString(), "symbols:", this.nodes.symbols, "unknownSymbols:", this.nodes.unknownSymbols );
            }
        }

        /**
         * Get the values of x and attributes referenced in this formula.
         * For evaluating a plot function y={some function of x}.
         * @param scope {*} - reusable scope object
         * @param xValue - point on plot to evaluate
         */
        getPlotFunctionScope( scope, xValue ) {
            Dyn.assert( this.nodes && this.isPlotFormula );
            scope.x = xValue; // all we need to do is update the x value in the scope, then call evaluate()
        }

        /** equivalent to evaluate() but using Nerdamer instead of MathJS.
         * NOTE: consider in the future to use just Nerdamer and drop MathJS, to simplify.
         * NOTE: We should consider using nerdamer.buildFunction(), it might be more efficient for plotting
         * or evaluating over many cases. See https://nerdamer.com/functions/Expression.buildFunction.html
         */
        evaluateNerdamer( scope, expectedType ) {
            //let localNerdamerForDebugging = nerdamer;
            let result;
            Dyn.assert( expectedType==='number' );
            try {
                // let Nerdamer evaluate it
                if( this.nerdamerString ) {
                    this.numberOfEvaluations++; // for debugging
                    let nerdScope = Dyn.dataUtil.clone( scope ); // nerdamer modifies the scope so we must copy it
                    let resultText = nerdamer( this.nerdamerString, nerdScope, "numer" ).text();
                    result = Formula.fixPrecision( Number( resultText ), Number.NaN ); // convert to finite or NaN
                    // better? result = nerdamer( this.nerdamerString ).evaluate( nerdScope, "numer" ).text();
                }
            }
            catch ( err ) {
                // show new evaluate error
                this.numberOfEvalErrors++;
                this.error.evaluate = this._errorMessageToLongString( err );
                result = this._errorMessageToShortString( err );
                if( Formula.debug ) {
                    this._errorMessageIsDivideByZero( err )
                        ? Formula.debugShowDivideByZeroErrors && console.log( "formula evaluate error {" + err.message + "} for " + this.nerdamerString )
                        : console.log( "formula evaluate error {" + err.message + "} for " + this.nerdamerString );
                }
            }
            return( Dyn.dataUtil.normalizeCaseValueString( result )); // make sure result is string, number or undefined
        }


        /**
         * Return an array of X-axis values to show the location of
         *      finite numeric roots, local min/max, and intercepts for the function of x.
         * Y-axis intercept: f(x) where x=0
         * X-axis intercept: f(x) where y=0 : solve for y=0 to get x value(s) if any
         * local Min/Max: solve slope(f(x)) where slope = 0, to get x value(s) if any
         * See https://nerdamer.com/functions/solveFor.html for limitations, e.g. not function of x, or not a polynomial, etc etc.
         * @param scope -- scope from getPlotFunctionScope()
         * @returns {*} -- array of finite numeric roots (length 0+)
         */
        rootsAndInterceptsOfFx( scope, rangeX ) {
            Dyn.assert( this.isPlotFormula, "expecting a plot formula"); // TODO: move to class PlotFormula
            let localMinMax = [], // array of x-axis values of local min,max (slope==0)
                intercepts =[0]; // add y-axis intercept value to start
            let formulaOfX, rootsFormula, rootsResult, slopeFormula, slopeResult, result = {};
            formulaOfX = rootsFormula = rootsResult = slopeFormula = slopeResult = "";
            //let localNerdamerForDebugging = nerdamer;
            function logError( err, formulaString ) {
                Formula.debug && console.log( "roots evaluation error {"+err.message+"} for "+formulaString, scope, rootsFormula, rootsResult, slopeFormula, slopeResult );
            }
            function resultAsArrayOfFiniteNumbers( resultText ) {
                // convert results array, e.g [ 0, "1.3", "2.345", "1.3*i", "NaN", "-Infinity" ],
                //      to array of finite numbers, discarding imaginary roots, etc.
                return resultText.substring( 1, rootsResult.length - 1 ).split(',')
                    .map((str) => Number(str))
                    .filter((n) => isFinite(n));
            }
            if( this.nerdamerString ) {
                try {
                    // get values from formula like 'a*x^2', {a:6.2}, convert to '6.2*x^2', then solve for 'x=0', and 'slope(x)=0'.
                    //rootsExpr     = 'roots(' + ( formulaOfX.toString() ) + ')'; // don't use roots(), see https://github.com/jiggzson/nerdamer/issues/510
                    let nerdScope = Dyn.dataUtil.clone( scope ); // copy because nerdamer changes it
                    delete nerdScope.x; // keep 'x' undefined
                    formulaOfX = nerdamer( this.nerdamerString ).evaluate( nerdScope ).toString(); // replace constants like 'a' with numbers like '6.2'
                    this.numberOfEvaluations++; // for debugging
                } catch ( err ) {
                    logError( err, this.nerdamerString );
                }
                if( formulaOfX ) {
                    try {
                        rootsFormula = 'solve(' + ( formulaOfX ) + ',x)';
                        rootsResult  = nerdamer( rootsFormula ).evaluate().text(); // solve for x=0 to get x-axis intercepts
                        intercepts = intercepts.concat( resultAsArrayOfFiniteNumbers( rootsResult )); // add finite numeric x-intercepts
                        this.numberOfEvaluations++; // for debugging
                    } catch ( err ) {
                        logError( err, this.nerdamerString );
                    }
                    try {
                        slopeFormula = 'solve(diff(' + ( formulaOfX ) + ',x),x)';
                        slopeResult  = nerdamer( slopeFormula ).evaluate().text(); // solve for derivative of x=0 to get local min, max
                        localMinMax = resultAsArrayOfFiniteNumbers( slopeResult ); // add finite numeric local min, max
                        this.numberOfEvaluations++; // for debugging
                    } catch ( err ) {
                        logError( err, this.nerdamerString );
                    }
                }
            }
            result.intercepts = removeInvalid( intercepts.concat(localMinMax) );
            // shorten long results for cyclical functions like y=sin(x); nerdamer typically reports 20-40 results for sin(x)
            if( result.intercepts.length >= 10 ) {
                let interceptsInRange = result.intercepts.filter( n => rangeX.isInRange(n));
                if( interceptsInRange.length >= 5 ) {
                    result.intercepts = interceptsInRange.slice( 0, 5 ); // show first five values, in range
                    result.sampled = true;
                } else {
                    result.intercepts = result.intercepts.slice( 0, 5 ); // show first five values
                    result.sampled = true;
                }
            }
            Formula.debug && console.log( "Dyn.formula.rootsAndInterceptsOfFx() : ",
                "intercepts ("+intercepts.length+")", "localMinMax ("+localMinMax.length+")", result );
            return result; // array of x-axis values which have x-intercepts, y-intercepts, or slope==0.

            function removeInvalid( array ) {
                // convert results array, e.g [ 0, "1.3", "2.345", "1.3*i", "NaN", "-Infinity" ],
                //      to array of unique finite numbers, discarding imaginary roots, duplicates, etc.
                return array.filter( (n,i,self) => ( isFinite(n) && (self.indexOf(n)===i)) );
            }
        }

        canEvaluate() {
            return !(this.isEmptyFormula() || (!this.nerdamerCompiled) || this.parseError);
        }

        /**
         * Convert a Nerdamer exception object to a case value string, so the user can see the error.
         * @param err
         * @returns {string}
         * @private
         */
        _errorMessageToShortString( err ) {
            if( /is not a function/.test( err.message )) {
                return Dyn.loc( "not a function" );
            } else {
                let short = /^[\w]+\s[\w]+/.exec( err.message );
                if( short ) {
                    return short[0];
                }
            }
            return err.message;
        }

        /**
         * Convert a Nerdamer exception object to a long string, so the user can see the error.
         */
        _errorMessageToLongString( err ) {
            // Nerdamer error, or tuva custom error: { name, message, stack }
            // for now just return the string, maybe in the future use other error information provided, e.g. err.stack
            Formula.debug && console.log( "_errorMessageToLongString", err.name, err.message );
            let isCustomTuvaError = (err?.name==='tuva' && err?.message);
            return isCustomTuvaError ? err?.message : Dyn.loc( "Function incomplete" );
        }

        _errorMessageIsDivideByZero( err ) {
            // return true if "Divide by zero" is in error message thrown by Nerdamer
            return /Division by zero/.test( err.message );
        }

        static unitTest() {
            let unitTester = new Dyn.UnitTester( "Dyn.Formula.unitTest()" );

            // test misc. Nerdamer function parsing
            function testNerdamerParse( parsed, expected ) {
                var result = parsed.text ? parsed.text() : parsed.toString();
                unitTester.test( result===expected, "NERDAmer formula parse error: "+" : '"+result+"' !== '"+expected+"'");
            }
            testNerdamerParse( nerdamer('x^2+2*(cos(x)+x*x)'), '2*cos(x)+3*x^2' );
            testNerdamerParse( nerdamer('cos(x)+(y-x)^2', {x:7}, ['expand', 'numer']), '(-7+y)^2+0.753902254343304582');
            testNerdamerParse( nerdamer('x+y=c').solveFor('y'), '-(-c+x)' );

            // test PlotFormula parsing
            let plotFormulas = [
                [ "x^2", 4, 16 ],
                [ "x^2+2*(cos(x)+x*x)", 7, 2*Math.cos(7)+3*7*7 ]
            ].forEach( (pf) => {
                let formula = new PlotFormula( pf[0], { x: NaN, y: NaN } );
                let scope = formula.findDependencies();
                formula.getPlotFunctionScope( scope, pf[1] ); // add x to scope
                let fr = formula.evaluate( scope, 'number' );
                unitTester.test( (pf[2] - fr) < 1e10, 'expect y = '+pf[0]+', x='+pf[1]+', y='+pf[2]+', got:' + fr );
            });

            unitTester.report();
            return true;
        }

    }

    class AttributeFormula extends Formula {

        constructor( formulaString ) {
            const formulaSyntax = 'MathJS';
            super( formulaString, formulaSyntax );

            // derived formula state
            this.mathJsString = ""; // save for debugging
            this.mathJsCompiled = null; // MathJS compiled formula
            // this.derivativeFormula = null; // MathJS-derived slopeOfFx formula string
            // this.derivativeCompiled = null; // MathJS-derived slopeOfFx compiled formula

            if( ! math.hasTuvaExtentions ) {
                this.extend_MathJS_Library();
            }
        }

        get isAttrFormula() { return true; }

        /**
         * convert attribute names and IDs to valid symbols in formula string
         * NOTE: we must be mindful of name collisions,
         * e.g. with attribute names 'a','b', 'a b', and formula 'a-b', does formula change to "a_b"?
         * attribute "<length squared>" saved as "length_squared", etc.
         */
        fixFormulaAttributeName( str, attribute ) {
            var attributeName   = "<" + attribute.name + ">", // to help avoid name collisions require bracket around name
                attributeID     = attribute.id,
                attributeSymbol = Formula._attributeIDtoSymbol( attributeID );
            return str
                .replaceAll( attributeName, attributeSymbol )
                .replaceAll( attributeID, attributeSymbol );
        }

        /**
         * Use MathJS to parse the formula string, get symbols.
         * @param [dataSet] {Dyn.DataSet || null } -- dataSet, required only if allowing attribute names in formula
         * @param [attributeName] {Dyn.Attribute || null } -- optional attribute name for error messages
         */
        parse( dataSet, attributeName ) {
            // initialize nodes and error state
            Dyn.assert( this.formulaSyntax === 'MathJS');
            this.nodes = new Nodes( attributeName );
            this.error = {};
            this.mathJsCompiled = null; // clear compiled formula

            // modify the formula string to be MathJS compatible
            let strMathJS = this.formulaString;
            dataSet?.attribs.forEach( (attr) => {
                strMathJS = this.fixFormulaAttributeName( strMathJS, attr );
            });
            let aggs = Aggregate.replaceAggregatesInString( strMathJS );
            if( aggs ) {
                strMathJS = aggs.formulaString; // replace aggregate functions with case-independent constants
                this.nodes.aggregates = aggs.aggregates; // save for calcAggregates() which gets constant values
            }
            this.mathJsString = strMathJS; // save for debugging

            // MathJS parse and analyze nodes of the formula (to find attributes, etc).
            let node;
            try {
                node = math.parse( strMathJS ); // 1st pass: MathJS parse
                this.nodes.findNodesMathJS( node ); // 2nd pass: find unknown symbols, etc.
                this.mathJsCompiled = node.compile(); // if compiled, we have a valid formula
            }
            catch(err) {
                // Formula.debug && console.warn( "formula parse error {"+err.message+"}", this.nodes.selfSymbol );
                this.error.parse = this._errorMessageToLongString( err );
            }
        }

        /**
         * Get the attribute case values for attributes referenced in this formula.
         * @param scope {*} - reusable scope object
         * @param index - case index
         * @param dataSet {Dyn.DataSet} - dataSet with the case values
         */
        getCaseScope( scope, index, dataSet ) {
            //var self=this;
            Dyn.assert( this.nodes && this.isAttrFormula );
            scope.has__missing__values = false; // clear flag
            this.nodes.attributeSymbols.forEach( function( symbol ){
                let attribute = Formula._getAttribute( symbol, dataSet ),
                    value;
                scope[symbol] = value = ( attribute ? attribute.cases[index] : Dyn.dataUtil.kMissingValue );
                if( Dyn.dataUtil.isMissing( value )) {
                    scope.has__missing__values = true; // at least one missing value in this case
                }
            });
        }

        evaluateMathJS( scope, expectedType ) {
            let result;
            Dyn.assert( this.mathJsCompiled, "expecting a compiled formula");
            try {
                // let MathJS evaluate it
                this.numberOfEvaluations++; // for debugging
                result = this.mathJsCompiled.eval( scope );
                result = Formula.fixPrecision( result, result ); // fix precision if finite numeric
                // check the type if asked for
                if( expectedType ) {
                    let type = math.typeof( result ); // 'number', 'string', 'boolean', 'Unit', 'Complex', etc. see http://mathjs.org/docs/datatypes/index.html
                    if( type !== expectedType ) {
                        this.numberOfEvalErrors++;
                        this.error.evaluate = Dyn.loc( "Unexpected result: {{mathJStype}} data type", { mathJStype: type } );
                        result = Dyn.dataUtil.kMissingValue;
                    }
                }
            } catch( err ) {
                // show new evaluate error
                this.numberOfEvalErrors++;
                this.error.evaluate = this._errorMessageToLongString( err );
                result = Dyn.dataUtil.kMissingValue;
                //console.log( "formula evaluate error {" + err.message + "}" + ( ( this.nodes && this.nodes.selfSymbol ) ? "" : " for " + this.nodes.selfSymbol ) );
            }
            return result;
        }

        /**
         * Convert a MathJS exception object to a case value string, so the user can see the error.
         * @param err
         * @returns {string}
         * @private
         */
        // _errorMessageToShortString( err ) {
        //     if( /is not a function/.test( err.message )) {
        //         return Dyn.loc( "not a function" );
        //     } else {
        //         let short = /^[\w]+\s[\w]+/.exec( err.message );
        //         if( short ) {
        //             return short[0];
        //         }
        //     }
        //     return err.message;
        // }

        /**
         * Convert a MathJS exception object to a long string, so the user can see the error.
         */
        _errorMessageToLongString( err ) {
            // for now just return the string, maybe in the future use other error information provided, e.g. err.stack
            return err.message;
        }


        static unitTest() {
            let unitTester = new Dyn.UnitTester( "Dyn.AttributeFormula.unitTest()" );

            // test aggregate functions
            let values = [ 1, 2, 3, 3, 3 ],
                aggFormulas = [
                    // formula, expected-result, optional equivalent mathJS formula.  Note that attribute name is ignored.
                    [ "mean(values)", 2.4 ],
                    [ "median(values)", 3],
                    [ "iqr(values)", 1 ],
                    [ "stdDev( values )", 0.89442719099992],
                    [ "meanAvDev( values )", 0.72 ],
                    [ "variance(values)", 0.8 ],
                    [ "sum(values)", 12 ],
                    [ "min(values)", 1 ],
                    [ "max(values)", 3 ],
                    [ "count(values)", 5 ],
                ];
            aggFormulas.forEach((formulaResult) => {
                let [str, expected /*, mathJSresult*/ ] = formulaResult;
                let aggs = Aggregate.replaceAggregatesInString( str );
                unitTester.test( aggs.aggregates.length === 1, "could not find 1 aggregate function in:"+str);
                aggs.aggregates.forEach( (agg) => {
                    let result = agg.calculate( values );
                    unitTester.test( Dyn.mathUtil.isClose(result,expected), agg.formulaString+" result:"+result+" !== "+expected );
                });
            });
            let agg3 = [ "1 + mean(v) - median(v) + count( v_V )", 3], // test with multiple aggregates in the formula
                agg3r = Aggregate.replaceAggregatesInString( agg3[0] );
            unitTester.test( agg3r.aggregates.length===agg3[1], agg3[0]+"did not find the "+agg3[1]+" aggregate formulas" );

            // test MathJS parsing
            function testMathJSEval( formulaString, expectedResult ) {
                let actualResult = math.eval( formulaString );
                unitTester.test( Dyn.mathUtil.isClose( actualResult, expectedResult ), formulaString + " result:" + actualResult + " !== " + expectedResult + " from MathJS" );
            }
            testMathJSEval( "mean(1,2,3,3,3)", 2.4 );
            testMathJSEval( "median(1,2,3,3,3)", 3 );
            testMathJSEval( "std(1,2,3,3,3)", 0.89442719099992 );

            unitTester.report();
            return true;
        }


    }

Dyn.PlotFormula = PlotFormula; // make class public
Dyn.AttributeFormula = AttributeFormula; // make class public

Dyn.debugPlotFormula_UnitTestHasRun = false;
Dyn.debugAttributeFormula_UnitTestHasRun = false;
setTimeout( () => { // delay the test until mathUtil.js is loaded
    if( Dyn.debugging ) {
        Dyn.debugPlotFormula_UnitTestHasRun = PlotFormula.unitTest();
        Dyn.debugAttributeFormula_UnitTestHasRun = AttributeFormula.unitTest();
    }
}, 100 );

};  // wrapDynFormula
